import { type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react'
import './Button.css'

export type ButtonVariant = 'primary' | 'ghost' | 'outline' | 'danger'

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  fullWidth?: boolean
  loading?: boolean
  children: ReactNode
}

interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant
  fullWidth?: boolean
  children: ReactNode
}

/**
 * Кнопка-ссылка: тот же вид, но настоящий `<a href>`.
 *
 * Нужна для схем вроде `sing-box://` и `tg://`: в standalone-PWA на iOS `window.open` с кастомной
 * схемой часто молча не срабатывает, а обычный якорь ОС перехватывает штатно. Живёт в этом файле,
 * потому что `.btn*`-классы объявлены в Button.css — компонент обязан импортировать CSS всех своих
 * классов, иначе стили уедут вместе с чужим чанком.
 *
 * `target` намеренно не задаётся по умолчанию: кастомную схему обрабатывает ОС, а лишний контекст
 * в standalone только мешает. Для http(s)-ссылок передавай `target="_blank" rel="noreferrer"` явно.
 */
export function ButtonLink({
  variant = 'primary',
  fullWidth = false,
  className = '',
  children,
  ...rest
}: LinkProps) {
  const classes = ['btn', `btn--${variant}`, fullWidth && 'btn--full', className]
    .filter(Boolean)
    .join(' ')

  return (
    <a {...rest} className={classes}>
      {children}
    </a>
  )
}

export function Button({
  variant = 'primary',
  fullWidth = false,
  loading = false,
  className = '',
  disabled,
  children,
  ...rest
}: Props) {
  const classes = [
    'btn',
    `btn--${variant}`,
    fullWidth && 'btn--full',
    loading && 'btn--loading',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button {...rest} disabled={disabled ?? loading} className={classes}>
      {loading ? (
        <span className="btn__loader" role="status" aria-label="загрузка">
          <span className="btn__dot" />
          <span className="btn__dot" />
          <span className="btn__dot" />
        </span>
      ) : (
        children
      )}
    </button>
  )
}
